/**
 * API Service - FastAPI Backend Integration
 * Handles all communication with the Python FastAPI backend
 */

import { apiErrorFromResponse, formatErrorDetail } from './apiError';

class ApiService {
  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000';
    this.apiUrl = `${this.baseUrl}/api`;
    this.authToken = null;
    // On-demand token provider (Auth0 getAccessTokenSilently); resolved before
    // each request so a stale/cleared cached token never strands protected calls.
    this.tokenProvider = null;

    // Debug logging
    console.log('🔧 API Service initialized:');
    console.log('   Base URL:', this.baseUrl);
    console.log('   API URL:', this.apiUrl);
    console.log('   Environment variables:', import.meta.env);
  }

  /**
   * Set Auth0 access token for API requests
   */
  setAuthToken(token) {
    this.authToken = token;
    console.log('🔑 Auth token set for API requests');
  }

  /**
   * Clear Auth0 access token
   */
  clearAuthToken() {
    this.authToken = null;
    console.log('🔑 Auth token cleared');
  }

  /**
   * Register / clear an async token provider (Auth0 getAccessTokenSilently).
   */
  setTokenProvider(fn) {
    this.tokenProvider = fn;
  }

  clearTokenProvider() {
    this.tokenProvider = null;
  }

  /**
   * Resolve a valid bearer token on demand; never throws (null = no auth header).
   */
  async resolveToken() {
    if (this.tokenProvider) {
      try {
        const token = await this.tokenProvider();
        if (token) {
          this.authToken = token;
          return token;
        }
      } catch (err) {
        console.warn('⚠️ Could not obtain a fresh access token:', err?.message || err);
      }
    }
    return this.authToken;
  }

  /** Readable message for any FastAPI `detail` shape (see services/apiError.js). */
  formatErrorDetail(detail) {
    return formatErrorDetail(detail);
  }


  /**
   * Generic API request handler
   */
  async request(endpoint, options = {}) {
    const url = `${this.apiUrl}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Resolve a valid token on demand (fresh from Auth0 when possible) so a
    // previously-cleared/expired cached token doesn't cause protected calls to 403.
    const token = await this.resolveToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const config = {
      headers,
      ...options,
    };

    console.log(`🌐 API Request: ${options.method || 'GET'} ${url}`);

    try {
      const response = await fetch(url, config);
      
      console.log(`✅ API Response: ${response.status} ${response.statusText}`);
      
      if (!response.ok) {
        throw await apiErrorFromResponse(response);
      }

      const data = await response.json();
      console.log(`📦 API Data:`, data);
      return data;
    } catch (error) {
      console.error(`❌ API Request failed: ${endpoint}`, error);
      throw error;
    }
  }

  /**
   * Health check endpoints
   */
  async getSystemHealth() {
    // Use direct fetch to avoid /api prefix for health endpoint
    const response = await fetch(`${this.baseUrl}/health`);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return await response.json();
  }

  async getServicesHealth() {
    return await this.request('/health/services');
  }

  /**
   * Mission planning endpoints
   */
  async createMissionPlan(missionData) {
    return await this.request('/missions/plan', {
      method: 'POST',
      body: JSON.stringify(missionData),
    });
  }

  async getMission(missionId) {
    return await this.request(`/missions/${missionId}`);
  }

  async getMissions() {
    return await this.request('/missions');
  }

  async updateMission(missionId, updates) {
    return await this.request(`/missions/${missionId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  /**
   * Simulation endpoints
   */
  async runSimulation(simulationData) {
    return await this.request('/simulation/run', {
      method: 'POST',
      body: JSON.stringify(simulationData),
    });
  }

  async getSimulationResults(simulationId) {
    return await this.request(`/simulation/${simulationId}/results`);
  }

  async getSimulations() {
    return await this.request('/simulation/runs');
  }

  /**
   * Authentication endpoints
   */
  async login(credentials) {
    return await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  async register(userData) {
    return await this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  async logout() {
    return await this.request('/auth/logout', {
      method: 'POST',
    });
  }

  /**
   * Weather service integration
   */
  async getWeatherData(lat, lng) {
    return await this.request(`/weather/current?lat=${lat}&lng=${lng}`);
  }

  /**
   * Terrain service integration
   */
  async getElevationProfile(startCoords, endCoords, points = 10) {
    const params = new URLSearchParams({
      start_lat: startCoords[0],
      start_lng: startCoords[1],
      end_lat: endCoords[0],
      end_lng: endCoords[1],
      points: points,
    });
    
    return await this.request(`/terrain/elevation?${params}`);
  }

  /**
   * Test endpoint connection
   */
  async testConnection() {
    try {
      // Try the basic health endpoint first
      console.log('🔍 Testing connection to:', `${this.baseUrl}/health`);
      
      const response = await fetch(`${this.baseUrl}/health`);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const health = await response.json();
      console.log('✅ Backend connection successful:', health);
      return true;
    } catch (error) {
      console.error('❌ Backend connection failed:', error);
      console.error('   Attempted URL:', `${this.baseUrl}/health`);
      console.error('   Error details:', error);
      return false;
    }
  }

  /**
   * ==========================================
   * AI AGENT ENDPOINTS (Phase 3)
   * ==========================================
   */

  /**
   * Check agent service health
   */
  async getAgentHealth() {
    return await this.request('/agents/health');
  }

  /**
   * Generate objectives from strategic guidance using ObjectiveTranslatorAgent
   */
  async generateObjectives(missionPlanId, strategicGuidance, context = null) {
    return await this.request('/agents/objectives/generate', {
      method: 'POST',
      body: JSON.stringify({
        mission_plan_id: missionPlanId,
        strategic_guidance: strategicGuidance,
        context: context,
      }),
    });
  }

  /**
   * Analyze target with TargetAnalystAgent
   */
  async analyzeTarget(targetId, desiredEffect = 'destroy') {
    return await this.request('/agents/targets/analyze', {
      method: 'POST',
      body: JSON.stringify({
        target_id: targetId,
        desired_effect: desiredEffect,
      }),
    });
  }

  /**
   * Get weaponeering recommendation from WeaponeeringAgent
   */
  async getWeaponeeringRecommendation(targetId, desiredPk = 0.90) {
    return await this.request('/agents/weaponeering/recommend', {
      method: 'POST',
      body: JSON.stringify({
        target_id: targetId,
        desired_pk: desiredPk,
      }),
    });
  }

  /**
   * Generate complete COA using MissionPlanningOrchestrator
   * This is the main AI-enhanced mission planning endpoint.
   * targetIds is required -- the route 422s without at least one resolvable target.
   */
  async generateCOA(missionPlanId, context = null, targetIds = []) {
    return await this.request('/agents/coa/generate', {
      method: 'POST',
      body: JSON.stringify({
        mission_plan_id: missionPlanId,
        target_ids: targetIds,
        context: context,
      }),
    });
  }

  /**
   * ==========================================
   * SIMULATION ENDPOINTS (Phase 4)
   * ==========================================
   */

  /**
   * Run Monte Carlo simulation on a COA
   */
  async runMonteCarloOnCOA(coaId, iterations = 1000) {
    return await this.request('/simulation/monte-carlo', {
      method: 'POST',
      body: JSON.stringify({
        coa_id: coaId,
        iterations: iterations,
      }),
    });
  }

  /**
   * Optimize COA using multi-objective optimization
   * Returns Pareto-optimal COA variants
   */
  async optimizeCOA(coaId, options = {}) {
    const {
      variants = 10,
      effectivenessWeight = 0.4,
      costWeight = 0.2,
      riskWeight = 0.25,
      timeWeight = 0.1,
      collateralWeight = 0.05,
    } = options;

    return await this.request('/simulation/optimize-coa', {
      method: 'POST',
      body: JSON.stringify({
        coa_id: coaId,
        variants_to_generate: variants,
        effectiveness_weight: effectivenessWeight,
        cost_weight: costWeight,
        risk_weight: riskWeight,
        time_weight: timeWeight,
        collateral_weight: collateralWeight,
      }),
    });
  }

  /**
   * Plan route with threat avoidance
   */
  async planRoute(startLat, startLon, targetLat, targetLon, threatZones = null, cruiseAltitude = 25000) {
    return await this.request('/simulation/plan-route', {
      method: 'POST',
      body: JSON.stringify({
        start_lat: startLat,
        start_lon: startLon,
        target_lat: targetLat,
        target_lon: targetLon,
        threat_zones: threatZones,
        cruise_altitude_ft: cruiseAltitude,
      }),
    });
  }

  /**
   * ==========================================
   * LEGACY/COMPATIBILITY
   * ==========================================
   */

  /**
   * Real-time mission planning with AI agents (legacy wrapper)
   */
  async planMissionWithAI(strategicGuidance, constraints = {}, preferences = {}) {
    const missionRequest = {
      strategic_guidance: strategicGuidance,
      constraints,
      preferences,
      timestamp: new Date().toISOString(),
    };

    try {
      console.log('🤖 Sending mission to AI planning system...', missionRequest);
      const result = await this.createMissionPlan(missionRequest);
      console.log('✅ AI mission planning completed:', result);
      return result;
    } catch (error) {
      console.error('❌ AI mission planning failed:', error);
      throw error;
    }
  }

  /**
   * Run Monte Carlo simulation on mission plan (legacy wrapper)
   */
  async runMonteCarloSimulation(missionPlan, iterations = 1000) {
    const simulationRequest = {
      mission_plan: missionPlan,
      iterations,
      confidence_level: 0.95,
      timestamp: new Date().toISOString(),
    };

    try {
      console.log('📊 Running Monte Carlo simulation...', simulationRequest);
      const result = await this.runSimulation(simulationRequest);
      console.log('✅ Simulation completed:', result);
      return result;
    } catch (error) {
      console.error('❌ Simulation failed:', error);
      throw error;
    }
  }
}

// Create singleton instance
const apiService = new ApiService();

export default apiService;